import { PublicClientApplication } from "@azure/msal-browser";
import { useGoogleLogin } from "@react-oauth/google";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, getErrorMessage, userApi } from "../../../api";
import FormCard from "../../../components/FormCard";
import PillButton from "../../../components/PillButton";
import { APIContext } from "../../../connector";
import { useToast } from "../../../hooks/useToast";
import logo from "../../../static/images/Logo.png";
import classes from "./Login.module.scss";

const getMicrosoftRedirectUri = () => {
  if (typeof window !== "undefined") {
    const isStaging = window.location.pathname.startsWith("/staging");
    return `${window.location.origin}${isStaging ? "/staging" : ""}/login`;
  }
  return undefined;
};

const Login = () => {
  const navigate = useNavigate();
  const { setUserData, setIsAuthenticated } = useContext(APIContext);
  const { openToast } = useToast();
  const [loginLoadingProvider, setLoginLoadingProvider] = useState(null);


  const handleGoogleSuccess = async (tokenResponse) => {
    try {
      // Use the new session-based login (handles both ID token and Access token)
      setLoginLoadingProvider("google");
      const data = await authApi.loginWithGoogle(tokenResponse);

      if (data.requires_registration) {
        // Redirect to registration with pre-filled data
        navigate("/register", {
          state: {
            ...data,
            access_token: tokenResponse.credential || tokenResponse.access_token
          }
        });
        return;
      }

      // Successful login - fetch user data (cookies are now set)
      const userData = await userApi.getCurrentUser();
      setUserData(userData);
      setIsAuthenticated(true);

    } catch (error) {
      console.error("Login error:", error);
      const message = getErrorMessage(error);
      openToast(message, 5000, "danger");
      setLoginLoadingProvider(null);
    }
  };

  const handleGoogleError = () => {
    setLoginLoadingProvider(null);
    openToast("Google Login Failed", 5000, "danger");
  };

  useEffect(() => {
  const handleMicrosoftRedirect = async () => {
    const hasMicrosoftCode =
      window.location.search.includes("code=") ||
      window.location.hash.includes("code=");

    if (!hasMicrosoftCode) {
      return;
    }

    try {
      setLoginLoadingProvider("microsoft");

      await msalInstance.initialize();

      let response = await msalInstance.handleRedirectPromise();

      if (!response) {
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length === 0) {
          setLoginLoadingProvider(null);
          return;
        }

        response = await msalInstance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0],
        });
      }

      const data = await authApi.loginWithMicrosoft(response.accessToken);

      if (data.requires_registration) {
        navigate("/register", {
          state: { ...data },
        });
        return;
      }

      const userData = await userApi.getCurrentUser();
      setUserData(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Microsoft redirect error:", error);
      const message = getErrorMessage(error);
      openToast(message, 5000, "danger");
      setLoginLoadingProvider(null);
    }
  };

  handleMicrosoftRedirect();
  }, [navigate, setUserData, setIsAuthenticated, openToast]);

  const handleMicrosoftLogin = async () => {
    try {

      setLoginLoadingProvider("microsoft");
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise();
      await msalInstance.loginRedirect({
        scopes: ["User.Read"],
        prompt: "select_account",
        redirectUri: getMicrosoftRedirectUri(),
      });



    } catch (error) {
      console.error("Microsoft login error:", error);
      const message = getErrorMessage(error);
      openToast(message, 5000, "danger");
      setLoginLoadingProvider(null);
    }
  };

const handleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  return (
    <>
      <div className={classes.container}>
        <img src={logo} alt="Logo" className={classes.logo} />
        <FormCard className={classes.loginCard}>
          <h1 className={classes.title}>Login</h1>
          <p className={classes.description}>
            It's easier to sign up now
          </p>
          <PillButton 
          className={classes.signinButton} 
          onClick={handleLogin}
          disabled={loginLoadingProvider !== null}
          >
          {loginLoadingProvider === "google" ? "Logging you in..." : "Sign in with Google"}
          </PillButton>
          <PillButton
            className={classes.signinButton}
            onClick={handleMicrosoftLogin}
            disabled={loginLoadingProvider !== null}
          >
            {loginLoadingProvider === "microsoft" ? "Logging you in..." : "Sign in with Microsoft"}
          </PillButton>
          {/* <div className={classes.googleBtnWrapper}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="filled_blue"
              shape="pill"
              text="continue_with"
              size="large"
              // width="280"
              logo_alignment="center"
            />
          </div> */}
        </FormCard>
      </div>
    </>
  );
};

const msalInstance = new PublicClientApplication({
  system: {
    allowNativeBroker: false,
  },
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_AUTH_CLIENT_ID,
    authority: "https://login.microsoftonline.com/organizations",
    redirectUri: getMicrosoftRedirectUri(),
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true,
  },
});


export default Login;
